/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useRef } from 'react'
import { Button } from '@cmsgov/ds-medicare-gov'
import { ReactComponent as CaretIcon } from '../../images/Icon_Caret.svg'

import { useDisclosure } from '../../hooks/useDisclosure'
import { Drawer } from '../Drawer/Drawer'
import { useTranslation } from 'react-i18next'

import { Link, ResourceList, ResourceNavItem, NavMenu } from '../../models/Navigation'
import hamburgerIcon from '../../images/Icon_Hamburger.svg'
import classNames from 'classnames'
import { ClickEventOptions, sendTealiumClickEvent } from '../../utils/sendTealiumEvent'

import { CATEGORIES } from '../../constants/analytics.json'

interface NavLinkListProps {
  analyticsCategory: string;
  label: string;
  description?: string;
  isLoggedIn: boolean;
  links: Link[];
  menuName: string;
}

const NavLinkList: React.FC<NavLinkListProps> = ({ label, description, isLoggedIn, links, menuName, analyticsCategory }) => {
  const handleLinkClick = (link: Link) => {
    const options: ClickEventOptions = {
      logged_in: isLoggedIn,
      text: link.label,
      target_location: link.href,
      heading: menuName,
      subheading: label,
      category: analyticsCategory
    }

    sendTealiumClickEvent(options)
  }

  return (
    <div className='ds-u-sans ds-u-margin--3'>
      <h2 className='ds-u-font-size--lead ch-c-mobile-menu__section-label ds-u-margin-bottom--2'>{label}</h2>

      {description && <p className='ds-u-font-size--base ds-u-color--base ds-u-margin-left--3 ds-u-margin-bottom--2'>{description}</p>}

      <ul className='ds-u-padding--0 ds-u-padding-left--3 ds-u-margin-top--2' style={{ listStyle: 'none' }}>
        {links.map((link: Link) => (
          <li key={link.id} className='ds-u-margin-bottom--2'>
            <a
              className='ch-c-link ds-u-color--primary ds-u-font-size--base ds-u-text-decoration--none'
              href={link.href}
              onClick={() => handleLinkClick(link)}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface SubNavProps {
  analyticsCategory: string;
  isLoggedIn: boolean;
  menu?: React.ReactNode;
  menuName: string;
  sections?: ResourceList[];
  onBack: () => void;
}

const SubNav: React.FC<SubNavProps> = ({ analyticsCategory, isLoggedIn, menuName, sections, onBack, menu }) => (
  <>
    <button autoFocus className='ch-c-link ds-u-color--primary ch-c-mobile-nav__option-btn' onClick={onBack} type='button'>
      <span className='ds-u-display--flex ds-u-align-items--center '>
        <CaretIcon className='ch-c-caret--left ch-u-height--20 ds-u-margin-right--1' />
        <span className='ch-u-font--rubik'>Back</span>
      </span>
    </button>

    <hr className='ds-u-margin-y--0 ds-u-margin-x--3 ds-u-border--0 ds-u-fill--gray-lighter' style={{ height: '1px' }} />

    {
      sections
        ? sections.map((section: ResourceList) => (
          <NavLinkList analyticsCategory={analyticsCategory} isLoggedIn={isLoggedIn} key={section.id} menuName={menuName} {...section} />)
        ) : menu
    }
  </>
)

interface MobileNavSubListProps {
  navList: any[];
  onNavOptionClick: ((input: any) => (void));
  isLoggedIn: boolean;
  bottomBorder: boolean;
}

const MobileNavSubList: React.FC<MobileNavSubListProps> = ({ navList, onNavOptionClick, isLoggedIn, bottomBorder }) => {
  const ulClasses = `ch-c-mobile-nav__options-list${bottomBorder ? ' ch-c-mobile-nav__options-list-separator' : ''}`

  return (
    <ul className={ulClasses}>
      {
        navList.map(navOption => {
          const handleBackClick = () => {
            onNavOptionClick(undefined)

            const options: ClickEventOptions = {
              logged_in: isLoggedIn,
              text: 'Back',
              target_location: 'Back to main mobile navigation',
              heading: navOption.label,
              category: CATEGORIES.MOBILE_NAV
            }

            sendTealiumClickEvent(options)
          }

          const subNav = (
            <SubNav
              analyticsCategory={navOption.analyticsCategory}
              isLoggedIn={isLoggedIn}
              menu={navOption.menu}
              menuName={navOption.label}
              sections={navOption.subsections}
              onBack={handleBackClick}
            />
          )

          const handleNavOptionClick = () => {
            onNavOptionClick(subNav)

            const options: ClickEventOptions = {
              logged_in: isLoggedIn,
              text: navOption.label,
              category: navOption.analyticsCategory,
              target_location: navOption.href
            }

            sendTealiumClickEvent(options)
          }

          return (
            <li key={navOption.id + navOption.label}>
              <MobileNavOption
                href={navOption.href}
                onClick={handleNavOptionClick}
                label={navOption.label}
                icon={navOption.icon}
                noCaret={!navOption.subsections && !navOption.menu}
              />
            </li>
          )
        })
      }
    </ul>
  )
}

interface MobileNavOptionProps {
  onClick?: () => void;
  label: string;
  noCaret: boolean;
  href?: string;
  icon?: string;
}

const MobileNavOption: React.FC<MobileNavOptionProps> = ({ href, onClick, label, noCaret, icon }) => {
  const anchorRef:React.RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null)
  const buttonRef:React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

  if (href) {
    return (
      <a
        href={href}
        className='ds-c-button ch-c-link ds-u-color--primary ds-u-text-decoration--none ch-c-mobile-nav__option-btn'
        ref={anchorRef}
        onClick={() => {
          if (anchorRef.current) {
            anchorRef.current.focus()
          }
          if (onClick) onClick()
        }}
      >

        <span className='ds-u-display--flex ds-u-align-items--center ds-u-justify-content--center'>
          {icon && <img className='ch-u-margin-right--8px' src={icon} alt={label} />}
          <span className='ch-u-height--20'>{label}</span>
        </span>
        {!noCaret && <span><CaretIcon className='ch-u-margin-bottom--2px ch-c-caret--right' /></span>}
      </a>
    )
  } else {
    return (
      <button
        className='ds-c-button ch-c-link ds-u-color--primary ds-u-text-decoration--none ch-c-mobile-nav__option-btn'
        ref={buttonRef}
        type='button'
        onClick={() => {
          if (buttonRef.current) {
            buttonRef.current.focus()
          }
          if (onClick) onClick()
        }}
      >

        <span className='ds-u-display--flex ds-u-align-items--center ds-u-justify-content--center'>
          {icon && <img className='ch-u-margin-right--8px' src={icon} alt={label} />}
          <span className='ch-u-height--20'>{label}</span>
        </span>
        {!noCaret && <span><CaretIcon className='ch-u-margin-bottom--2px ch-c-caret--right' /></span>}
      </button>
    )
  }
}

interface PrimaryNavProps {
  navLists: any[][];
  isLoggedIn: boolean;
  languageToggleLink: string;
  onNavOptionClick: (arg: any) => void;
  loginUrl: string;
  logoutUrl: string;
}

const PrimaryNav: React.FC<PrimaryNavProps> = ({ navLists, isLoggedIn, languageToggleLink, onNavOptionClick, loginUrl, logoutUrl }) => {
  const { t } = useTranslation('common')

  return (
    <>
      {
        navLists.map((navList, index) => {
          const bottomBorder = index < (navLists.length - 1)
          return (
            <MobileNavSubList navList={navList} onNavOptionClick={onNavOptionClick} isLoggedIn={isLoggedIn} key={index} bottomBorder={bottomBorder} />
          )
        })
      }

      <div>
        <MobileNavOption
          href={languageToggleLink}
          label={t('changeLanguage')}
          noCaret
          onClick={() => {
            const options: ClickEventOptions = {
              logged_in: isLoggedIn,
              text: t('changeLanguage'),
              target_location: languageToggleLink,
              category: CATEGORIES.LANGUAGE_SELECTION
            }

            sendTealiumClickEvent(options)
          }}
        />
      </div>

      <div className='ch-u-p--20px'>
        {isLoggedIn
          ? (
            <Button
              className='ch-u-border-box ch-c-button--primary-alt-outline ch-u-width--full'
              href={logoutUrl}
              onClick={() => {
                const options: ClickEventOptions = {
                  logged_in: true,
                  text: t('Log out'),
                  target_location: logoutUrl,
                  category: CATEGORIES.PERSONAL_NAV
                }
                sendTealiumClickEvent(options)
              }}
            >
              {t('Log out')}
            </Button>
          )
          : (
            <Button
              className='ch-u-border-box ch-c-button--primary ch-u-width--full ch-u-color--white'
              href={loginUrl}
              onClick={() => {
                const options: ClickEventOptions = {
                  logged_in: false,
                  text: t('Log in'),
                  target_location: loginUrl,
                  category: CATEGORIES.PERSONAL_NAV
                }
                sendTealiumClickEvent(options)
              }}
            >
              {t('Log in')}
            </Button>
          )}
      </div>
    </>
  )
}

export interface MobileNavigationProps {
  className?: string;
  loginUrl: string;
  logoutUrl: string;
  isLoggedIn?: boolean;
  navLists: (ResourceNavItem | Link | NavMenu)[][];
  languageToggleLink: string;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ isLoggedIn = false, loginUrl, logoutUrl, navLists, className, languageToggleLink }) => {
  const [subNav, setSubNav] = useState<any | undefined>(undefined)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const openBtnRef: React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)

  const handleMenuButtonClick = () => {
    onOpen()

    const options: ClickEventOptions = {
      text: 'Menu',
      logged_in: isLoggedIn,
      category: CATEGORIES.MOBILE_NAV,
      target_location: 'Open mobile navigation'
    }

    sendTealiumClickEvent(options)
  }

  const handleCloseButtonClick = () => {
    onClose()
    setSubNav(undefined)

    if (openBtnRef.current) openBtnRef.current.focus()

    const options: ClickEventOptions = {
      text: 'X',
      logged_in: isLoggedIn,
      category: CATEGORIES.MOBILE_NAV,
      target_location: 'Close mobile navigation'
    }

    sendTealiumClickEvent(options)
  }

  return (
    <>
      <button
        className={classNames('ds-c-button ch-c-button--primary-outline', 'ch-c-button--mobile-menu', className)}
        onClick={handleMenuButtonClick}
        ref={openBtnRef}
        data-testid='mobile-nav-trigger'
        aria-expanded={isOpen}
        type='button'
      >
        <span className='nav-menu-button-content-container ds-u-display--flex ds-u-align-items--center ds-u-font-size--base'>
          <img src={hamburgerIcon} alt='menu' width='24px' height='20px' className='ch-c-button--space-between' data-testid='veggieburger' />
          Menu
        </span>
      </button>

      <Drawer className={className} title='Menu' onClose={handleCloseButtonClick} isOpen={isOpen}>
        <nav>
          {subNav || <PrimaryNav onNavOptionClick={setSubNav} isLoggedIn={!!isLoggedIn} loginUrl={loginUrl} logoutUrl={logoutUrl} navLists={navLists} languageToggleLink={languageToggleLink} />}
        </nav>
      </Drawer>
    </>
  )
}
